/**
 * useFollowUps Hook
 *
 * Hook para gerenciar follow-ups de calendário com React Query
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { calendarService } from '../services/calendarService';

/**
 * Hook para criar follow-up
 */
export const useCreateFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => calendarService.createFollowUp(data),
    onSuccess: (_data, variables) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });

      // Se temos contactId, invalidar follow-ups específicos desse contato
      if (variables.contactId) {
        queryClient.invalidateQueries({
          queryKey: ['follow-ups', 'contact', variables.contactId]
        });
      }
    }
  });
};

/**
 * Hook para atualizar follow-up
 */
export const useUpdateFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      calendarService.updateFollowUp(id, data),
    onSuccess: (_data, variables) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });

      // Se temos contactId, invalidar follow-ups específicos desse contato
      if (variables.data.contactId) {
        queryClient.invalidateQueries({
          queryKey: ['follow-ups', 'contact', variables.data.contactId]
        });
      }
    }
  });
};

/**
 * Hook para deletar follow-up
 */
export const useDeleteFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => calendarService.deleteFollowUp(id),
    onSuccess: () => {
      // Invalidar todas as queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      queryClient.invalidateQueries({ queryKey: ['follow-ups'] });
    }
  });
};

/**
 * Hook para buscar follow-ups de um contato específico
 */
export const useFollowUpsByContact = (contactId: string | undefined) => {
  return useQuery({
    queryKey: ['follow-ups', 'contact', contactId],
    queryFn: () => calendarService.getFollowUpsByContact(contactId!),
    enabled: !!contactId, // Só executa se contactId estiver definido
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook agregado que expõe todas as funcionalidades de follow-ups
 */
export const useFollowUps = (contactId?: string) => {
  const createFollowUp = useCreateFollowUp();
  const followUps = useFollowUpsByContact(contactId);

  return {
    // Queries
    followUps: followUps.data || [],
    isLoading: followUps.isLoading,
    isError: followUps.isError,
    error: followUps.error,
    refetch: followUps.refetch,

    // Mutations
    createFollowUp: createFollowUp.mutate,
    createFollowUpAsync: createFollowUp.mutateAsync,
    isCreating: createFollowUp.isPending,
    createError: createFollowUp.error,
  };
};
